import { getUniqueSortedFloorsFromInventory, normalizeFloorValue } from "./utils";

export const INVENTORY_STATUS = {
  rtb: "RTB",
  rts: "RTS",
};

const UNAVAILABLE_INVENTORY_STATUSES = new Set([
  "draft booking",
  "booked",
  "sold",
  "booking",
  "registered",
  "on hold",
  "blocked",
  "hold",
  "reserved",
  "not available",
  "unavailable",
  "inactive",
  "deactive",
  "deactivated",
]);

/** JW inventory bucket fields – lifecycle-only fields are excluded. */
const AVAILABILITY_CATEGORY_FIELDS = [
  "Availability Status",
  "Unit Status",
  "Inventory Status",
  "Unit Availability",
  "Availability Type",
  "Unit Category",
  "Flat Category",
  "Availability",
];

/** Often booking/sale workflow fields – not the RTB/RTS inventory bucket. */
const LOW_PRIORITY_CATEGORY_FIELDS = ["Booking Status", "Sale Status"];

/** Lifecycle occupancy fields (Available / Booked / Sold / On Hold). */
const FLAT_LIFECYCLE_FIELDS = ["Flat Status", "Unit Lifecycle Status"];

const LIFECYCLE_STATUS_FIELDS = ["Status", "state", "State"];

const STATUS_KEY_PATTERN =
  /status|availability|state|category|rtb|rts|book|sell/i;

function isLowPriorityCategoryField(field) {
  const lower = field.toLowerCase();
  return LOW_PRIORITY_CATEGORY_FIELDS.some(
    (candidate) => candidate.toLowerCase() === lower,
  );
}

function isPrimaryCategoryField(field) {
  const lower = field.toLowerCase();
  return AVAILABILITY_CATEGORY_FIELDS.some(
    (candidate) => candidate.toLowerCase() === lower,
  );
}

function collectAvailabilityCategoryMatches(item) {
  const matches = [];
  const seen = new Set();

  const addMatch = (sourceField, value) => {
    const code = resolveInventoryAvailabilityCode(value);
    if (!code) {
      return;
    }

    const key = `${sourceField}:${code}`;
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    matches.push({
      raw: String(value).trim(),
      sourceField,
      code,
    });
  };

  for (const field of AVAILABILITY_CATEGORY_FIELDS) {
    const value = getFieldValue(item, field);
    if (value != null) {
      addMatch(field, value);
    }
  }

  const rtbFlag = getFieldValue(item, "RTB");
  if (
    isExactRtbOrRtsAbbreviation(rtbFlag) ||
    isTruthyAvailabilitySelection(rtbFlag)
  ) {
    addMatch("RTB", INVENTORY_STATUS.rtb);
  }

  const rtsFlag = getFieldValue(item, "RTS");
  if (
    isExactRtbOrRtsAbbreviation(rtsFlag) ||
    isTruthyAvailabilitySelection(rtsFlag)
  ) {
    addMatch("RTS", INVENTORY_STATUS.rts);
  }

  for (const [key, value] of Object.entries(item)) {
    if (
      STATUS_KEY_PATTERN.test(key) &&
      !LIFECYCLE_STATUS_FIELDS.includes(key) &&
      !isPrimaryCategoryField(key) &&
      !isLowPriorityCategoryField(key) &&
      value != null
    ) {
      addMatch(key, value);
    }
  }

  for (const field of LOW_PRIORITY_CATEGORY_FIELDS) {
    const value = getFieldValue(item, field);
    if (value != null) {
      addMatch(field, value);
    }
  }

  return matches;
}

function pickBestCategoryMatch(matches) {
  if (!matches.length) {
    return null;
  }

  return matches[0];
}

function isExactRtbOrRtsAbbreviation(value) {
  const upper = String(value ?? "")
    .trim()
    .toUpperCase();
  return upper === INVENTORY_STATUS.rtb || upper === INVENTORY_STATUS.rts;
}

function isTruthyAvailabilitySelection(value) {
  if (value === true || value === 1) {
    return true;
  }

  const normalized = String(value ?? "").trim().toLowerCase();
  return (
    normalized === "true" ||
    normalized === "yes" ||
    normalized === "y" ||
    normalized === "1" ||
    normalized === "selected"
  );
}

function inferRtbRtsCodeFromItem(item) {
  const resolved = getInventoryAvailabilityLabel(item);
  if (resolved === INVENTORY_STATUS.rtb || resolved === INVENTORY_STATUS.rts) {
    return resolved;
  }

  const rtsFlag = getFieldValue(item, "RTS");
  if (
    isExactRtbOrRtsAbbreviation(rtsFlag) ||
    isTruthyAvailabilitySelection(rtsFlag)
  ) {
    return INVENTORY_STATUS.rts;
  }

  const rtbFlag = getFieldValue(item, "RTB");
  if (
    isExactRtbOrRtsAbbreviation(rtbFlag) ||
    isTruthyAvailabilitySelection(rtbFlag)
  ) {
    return INVENTORY_STATUS.rtb;
  }

  for (const value of Object.values(item)) {
    if (isExactRtbOrRtsAbbreviation(value)) {
      const upper = String(value).trim().toUpperCase();
      return upper === INVENTORY_STATUS.rts
        ? INVENTORY_STATUS.rts
        : INVENTORY_STATUS.rtb;
    }

    const normalized = normalizeInventoryStatusValue(value);
    if (isRtsStatus(normalized)) {
      return INVENTORY_STATUS.rts;
    }
    if (isRtbStatus(normalized)) {
      return INVENTORY_STATUS.rtb;
    }
  }

  const status = normalizeInventoryStatusValue(getFieldValue(item, "Status"));
  if (isRtsStatus(status)) {
    return INVENTORY_STATUS.rts;
  }
  if (isRtbStatus(status)) {
    return INVENTORY_STATUS.rtb;
  }

  return "";
}

export function resolveInventoryFlatName(item) {
  const flatName = String(item?.["Flat Name"] ?? "").trim();
  if (flatName) {
    return flatName;
  }

  const wing = String(item?.Wing ?? "").trim();
  const flatNumber = String(item?.["Flat Number"] ?? "").trim();
  if (wing && flatNumber) {
    return `${wing}-${flatNumber}`;
  }

  return flatNumber;
}

function normalizeInventoryStatusValue(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isUnavailableInventoryStatus(value) {
  if (!value) {
    return false;
  }

  if (isRtbStatus(value) || isRtsStatus(value) || value === "available") {
    return false;
  }

  if (UNAVAILABLE_INVENTORY_STATUSES.has(value)) {
    return true;
  }

  return false;
}

function isRtbStatus(value) {
  return (
    value === "rtb" ||
    value === "ready to book" ||
    value.includes("ready to book")
  );
}

function isRtsStatus(value) {
  return (
    value === "rts" ||
    value === "ready to sell" ||
    value.includes("ready to sell")
  );
}

function isAvailableLifecycleStatus(value) {
  // JW may set Flat Status to Available or Cancelled after a booking cancel.
  return (
    value === "available" || value === "cancelled" || value === "canceled"
  );
}

export function resolveInventoryAvailabilityCode(rawStatus) {
  const normalized = normalizeInventoryStatusValue(rawStatus);

  if (!normalized) {
    return "";
  }

  if (isRtbStatus(normalized)) {
    return INVENTORY_STATUS.rtb;
  }

  if (isRtsStatus(normalized)) {
    return INVENTORY_STATUS.rts;
  }

  if (isUnavailableInventoryStatus(normalized)) {
    return "";
  }

  const upper = String(rawStatus ?? "")
    .trim()
    .toUpperCase();

  if (upper === INVENTORY_STATUS.rtb) {
    return INVENTORY_STATUS.rtb;
  }

  if (upper === INVENTORY_STATUS.rts) {
    return INVENTORY_STATUS.rts;
  }

  return "";
}

function getFieldValue(item, field) {
  if (item?.[field] != null && String(item[field]).trim() !== "") {
    return item[field];
  }

  const lowerField = field.toLowerCase();
  for (const [key, value] of Object.entries(item)) {
    if (key.toLowerCase() === lowerField && String(value ?? "").trim() !== "") {
      return value;
    }
  }

  return undefined;
}

function findAvailabilityCategoryRaw(item) {
  const statusValue = getFieldValue(item, "Status");
  if (statusValue != null && isExactRtbOrRtsAbbreviation(statusValue)) {
    return { raw: String(statusValue).trim(), sourceField: "Status" };
  }

  const matches = collectAvailabilityCategoryMatches(item);
  const best = pickBestCategoryMatch(matches);

  return best ? { raw: best.raw, sourceField: best.sourceField } : null;
}

export function getInventoryStatusRaw(item) {
  const categoryMatch = findAvailabilityCategoryRaw(item);
  if (categoryMatch) {
    return categoryMatch.raw;
  }

  const lifecycleStatus = normalizeInventoryStatusValue(
    getFieldValue(item, "Status"),
  );

  if (isAvailableLifecycleStatus(lifecycleStatus)) {
    return String(getFieldValue(item, "Status") ?? "").trim();
  }

  for (const field of LIFECYCLE_STATUS_FIELDS) {
    const value = getFieldValue(item, field);
    if (value != null && resolveInventoryAvailabilityCode(value) !== "") {
      return String(value).trim();
    }
  }

  const lifecycle = getFieldValue(item, "Status");
  return lifecycle != null ? String(lifecycle).trim() : "";
}

export function getInventoryAvailabilityLabel(item) {
  return resolveInventoryAvailabilityCode(getInventoryStatusRaw(item));
}

function getFlatLifecycleStatus(item) {
  for (const field of FLAT_LIFECYCLE_FIELDS) {
    const value = getFieldValue(item, field);
    if (value != null && String(value).trim() !== "") {
      return normalizeInventoryStatusValue(value);
    }
  }
  return "";
}

export function isOnHoldInventoryFlat(item) {
  const flatLifecycle = getFlatLifecycleStatus(item);
  return flatLifecycle === "on hold" || flatLifecycle === "hold";
}

const INACTIVE_INVENTORY_FLAG_FIELDS = [
  "Active",
  "Is Active",
  "Active Status",
  "Inventory Active",
  "Unit Active",
  "Inventory Active Status",
];

function isInactiveInventoryFlagValue(value) {
  const normalized = normalizeInventoryStatusValue(value);
  return (
    normalized === "inactive" ||
    normalized === "deactive" ||
    normalized === "deactivated" ||
    normalized === "disabled" ||
    normalized === "no" ||
    normalized === "n" ||
    normalized === "false" ||
    normalized === "0" ||
    normalized === "off"
  );
}

export function isInactiveInventoryFlat(item) {
  for (const field of INACTIVE_INVENTORY_FLAG_FIELDS) {
    const value = getFieldValue(item, field);
    if (value == null || String(value).trim() === "") {
      continue;
    }
    if (isInactiveInventoryFlagValue(value)) {
      return true;
    }
  }

  const status = normalizeInventoryStatusValue(getFieldValue(item, "Status"));
  return (
    status === "inactive" ||
    status === "deactive" ||
    status === "deactivated"
  );
}

const BLOCKED_FLAT_LIFECYCLE_STATUSES = new Set([
  "booked",
  "sold",
  "blocked",
  "registered",
  "reserved",
  "not available",
  "unavailable",
  "draft booking",
  "inactive",
  "deactive",
  "deactivated",
]);

const INVENTORY_OCCUPANCY_FIELDS = [
  "Flat Status",
  "Unit Lifecycle Status",
  "Inventory Status",
  "Availability Status",
  "Unit Status",
  "Unit Availability",
  "Availability",
  "Unit Category",
  "Flat Category",
  "Availability Type",
  "Booking Status",
  "Sale Status",
];

function getInventoryOccupancyStatuses(item) {
  const statuses = [];

  for (const field of INVENTORY_OCCUPANCY_FIELDS) {
    const value = getFieldValue(item, field);
    if (value == null || String(value).trim() === "") {
      continue;
    }

    statuses.push(normalizeInventoryStatusValue(value));
  }

  return statuses;
}

function isBlockedInventoryFlatForBooking(item) {
  if (isInactiveInventoryFlat(item) || isOnHoldInventoryFlat(item)) {
    return true;
  }

  // Flat Status is the occupancy source of truth. After Booked → Available in JW,
  // stale Booking Status / Sale Status must not keep the flat hidden.
  const flatLifecycle = getFlatLifecycleStatus(item);
  if (isAvailableLifecycleStatus(flatLifecycle)) {
    return false;
  }
  if (flatLifecycle && BLOCKED_FLAT_LIFECYCLE_STATUSES.has(flatLifecycle)) {
    return true;
  }

  for (const occupancy of getInventoryOccupancyStatuses(item)) {
    if (BLOCKED_FLAT_LIFECYCLE_STATUSES.has(occupancy)) {
      return true;
    }

    if (
      isUnavailableInventoryStatus(occupancy) &&
      !isRtbStatus(occupancy) &&
      !isRtsStatus(occupancy) &&
      occupancy !== "available"
    ) {
      return true;
    }
  }

  return false;
}

export function isEligibleBookingInventoryFlat(item) {
  if (isBlockedInventoryFlatForBooking(item)) {
    return false;
  }

  const availabilityCode = inferRtbRtsCodeFromItem(item);
  if (
    availabilityCode === INVENTORY_STATUS.rtb ||
    availabilityCode === INVENTORY_STATUS.rts
  ) {
    return true;
  }

  // JW often restores Flat Status to Available without re-tagging RTB/RTS yet.
  const flatLifecycle = getFlatLifecycleStatus(item);
  if (isAvailableLifecycleStatus(flatLifecycle)) {
    return true;
  }

  const legacyLifecycle = normalizeInventoryStatusValue(
    getFieldValue(item, "Status"),
  );
  return isAvailableLifecycleStatus(legacyLifecycle);
}

export function filterEligibleBookingInventoryFlats(inventory, options = {}) {
  if (!Array.isArray(inventory)) {
    return [];
  }

  const excluded = new Set(
    (options.excludeFlatNames ?? [])
      .map((name) => String(name ?? "").trim())
      .filter(Boolean),
  );

  const flats = inventory.reduce((acc, item) => {
    if (!item || typeof item !== "object") {
      return acc;
    }

    const flatName = resolveInventoryFlatName(item);

    if (!flatName || excluded.has(flatName)) {
      return acc;
    }

    if (!matchesInventoryFlatType(item, options.flatType)) {
      return acc;
    }

    if (!matchesInventoryFloor(item, options.floor)) {
      return acc;
    }

    if (!isEligibleBookingInventoryFlat(item)) {
      return acc;
    }

    acc.push(item);
    return acc;
  }, []);

  return sortInventoryFlatsForDisplay(flats);
}

export function getEligibleFloorsForConfiguration(inventory, flatType) {
  if (!flatType) {
    return [];
  }

  return getUniqueSortedFloorsFromInventory(
    filterEligibleBookingInventoryFlats(Array.isArray(inventory) ? inventory : [], {
      flatType,
    }),
  );
}

export function mergeInventoryCatalog(existing, incoming) {
  const byFlatName = new Map();

  for (const item of [...(existing ?? []), ...(incoming ?? [])]) {
    if (!item || typeof item !== "object") {
      continue;
    }

    const flatName = String(item["Flat Name"] ?? "").trim();
    if (!flatName) {
      continue;
    }

    byFlatName.set(flatName, item);
  }

  return Array.from(byFlatName.values());
}

export function isBookableInventoryFlat(item) {
  if (isInactiveInventoryFlat(item) || isOnHoldInventoryFlat(item)) {
    return false;
  }

  const flatLifecycle = getFlatLifecycleStatus(item);

  // Flat Status is source of truth for occupancy (Booked / Sold / On Hold / etc.).
  if (flatLifecycle && isUnavailableInventoryStatus(flatLifecycle)) {
    return false;
  }

  if (resolveInventoryAvailabilityCode(getInventoryStatusRaw(item)) !== "") {
    return true;
  }

  if (isAvailableLifecycleStatus(flatLifecycle)) {
    return true;
  }

  const legacyLifecycle = normalizeInventoryStatusValue(
    getFieldValue(item, "Status"),
  );

  if (legacyLifecycle && isUnavailableInventoryStatus(legacyLifecycle)) {
    return false;
  }

  if (isAvailableLifecycleStatus(legacyLifecycle)) {
    return true;
  }

  if (isRtbStatus(legacyLifecycle) || isRtsStatus(legacyLifecycle)) {
    return true;
  }

  // JW rows are selectable unless occupancy is explicitly blocked above.
  if (!flatLifecycle) {
    return true;
  }

  return !isUnavailableInventoryStatus(flatLifecycle);
}

export function normalizeInventoryFlatType(value) {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, "")
    .toLowerCase();
}

export function getUniqueFlatTypesFromInventory(inventory) {
  if (!Array.isArray(inventory)) {
    return [];
  }

  const seen = new Map();

  for (const item of inventory) {
    if (!item || typeof item !== "object") {
      continue;
    }

    if (isInactiveInventoryFlat(item)) {
      continue;
    }

    const label = String(item["Flat Type"] ?? "").trim();
    if (!label) {
      continue;
    }

    const key = normalizeInventoryFlatType(label);
    if (!seen.has(key)) {
      seen.set(key, label);
    }
  }

  return Array.from(seen.values());
}

export function matchesInventoryFlatType(item, flatType) {
  if (!flatType) {
    return true;
  }

  const itemFlatType = item?.["Flat Type"];
  // JW inventory uses mixed string/number field values.
  // eslint-disable-next-line eqeqeq
  if (itemFlatType == flatType) {
    return true;
  }

  return (
    normalizeInventoryFlatType(itemFlatType) ===
    normalizeInventoryFlatType(flatType)
  );
}

export function matchesInventoryFloor(item, floor) {
  const selected = normalizeFloorValue(floor);
  if (!selected) {
    return true;
  }

  return normalizeFloorValue(item?.Floor) === selected;
}

function sortInventoryFlatsForDisplay(flats) {
  return [...flats].sort((a, b) => {
    const aLabel = getInventoryAvailabilityLabel(a);
    const bLabel = getInventoryAvailabilityLabel(b);
    const aRank =
      aLabel === INVENTORY_STATUS.rtb
        ? 0
        : aLabel === INVENTORY_STATUS.rts
          ? 1
          : 2;
    const bRank =
      bLabel === INVENTORY_STATUS.rtb
        ? 0
        : bLabel === INVENTORY_STATUS.rts
          ? 1
          : 2;
    if (aRank !== bRank) {
      return aRank - bRank;
    }
    return String(a["Flat Name"] ?? "").localeCompare(
      String(b["Flat Name"] ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" },
    );
  });
}

export function filterInventoryFlatsForBookingSelection(inventory, options = {}) {
  if (!Array.isArray(inventory)) {
    return [];
  }

  const excluded = new Set(
    (options.excludeFlatNames ?? [])
      .map((name) => String(name ?? "").trim())
      .filter(Boolean),
  );

  const flats = inventory.filter((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const flatName = String(item["Flat Name"] ?? "").trim();
    if (!flatName || excluded.has(flatName)) {
      return false;
    }

    if (!matchesInventoryFlatType(item, options.flatType)) {
      return false;
    }

    if (!matchesInventoryFloor(item, options.floor)) {
      return false;
    }

    return !isBlockedInventoryFlatForBooking(item);
  });

  return sortInventoryFlatsForDisplay(flats);
}

export function filterInventoryFlatsForFloorOptions(inventory, options = {}) {
  if (!Array.isArray(inventory)) {
    return [];
  }

  return inventory.filter((item) => {
    if (!item || typeof item !== "object") {
      return false;
    }

    const flatName = String(item["Flat Name"] ?? "").trim();
    if (!flatName) {
      return false;
    }

    return matchesInventoryFlatType(item, options.flatType);
  });
}

export function filterBookableInventoryFlats(inventory, options = {}) {
  if (!Array.isArray(inventory)) {
    return [];
  }

  const excluded = new Set(
    (options.excludeFlatNames ?? [])
      .map((name) => String(name ?? "").trim())
      .filter(Boolean),
  );

  return inventory
    .filter((item) => {
      if (!item || typeof item !== "object") {
        return false;
      }

      const flatName = String(item["Flat Name"] ?? "").trim();

      if (!flatName || excluded.has(flatName)) {
        return false;
      }

      if (!matchesInventoryFlatType(item, options.flatType)) {
        return false;
      }

      if (!matchesInventoryFloor(item, options.floor)) {
        return false;
      }

      return isBookableInventoryFlat(item);
    })
    .sort((a, b) => {
      const aLabel = getInventoryAvailabilityLabel(a);
      const bLabel = getInventoryAvailabilityLabel(b);
      const aRank =
        aLabel === INVENTORY_STATUS.rtb
          ? 0
          : aLabel === INVENTORY_STATUS.rts
            ? 1
            : 2;
      const bRank =
        bLabel === INVENTORY_STATUS.rtb
          ? 0
          : bLabel === INVENTORY_STATUS.rts
            ? 1
            : 2;
      if (aRank !== bRank) {
        return aRank - bRank;
      }
      return String(a["Flat Name"] ?? "").localeCompare(
        String(b["Flat Name"] ?? ""),
      );
    });
}

export function isRtbInventoryStatus(statusCode) {
  return statusCode === INVENTORY_STATUS.rtb;
}

/** Display parts for flat dropdown rows – keeps identification consistent across UIs. */
export function getInventoryFlatDisplayParts(item) {
  const flatName = String(item?.["Flat Name"] ?? "").trim();
  const wing = String(item?.Wing ?? "").trim();
  const flatNumber = String(item?.["Flat Number"] ?? "").trim();
  const floor = String(item?.Floor ?? "").trim();
  const flatType = String(item?.["Flat Type"] ?? "").trim();
  const carpetArea = String(
    item?.["Carpet Area"] ?? item?.Carpet ?? "",
  ).trim();
  const availabilityLabel = getInventoryAvailabilityLabel(item);

  const metaParts = [];
  if (wing) {
    metaParts.push(`Wing ${wing}`);
  }
  if (flatType) {
    metaParts.push(flatType);
  }
  if (flatNumber && (!flatName || !flatName.endsWith(flatNumber))) {
    metaParts.push(`No. ${flatNumber}`);
  }
  if (carpetArea) {
    metaParts.push(`${carpetArea} Sq.ft`);
  }

  return {
    flatName,
    wing,
    flatNumber,
    floor,
    flatType,
    carpetArea,
    availabilityLabel,
    metaLine: metaParts.join(" · "),
    optionLabel: [
      flatName,
      availabilityLabel ? `(${availabilityLabel})` : "",
    ]
      .filter(Boolean)
      .join(" "),
    detailLabel: [
      flatName,
      availabilityLabel ? `(${availabilityLabel})` : "",
      metaParts.length ? `— ${metaParts.join(" · ")}` : "",
    ]
      .filter(Boolean)
      .join(" "),
  };
}

export function enrichInventoryFlatForDropdown(item) {
  const display = getInventoryFlatDisplayParts(item);

  return {
    ...item,
    optionLabel: display.optionLabel,
    dropdownMeta: display.metaLine,
    availabilityLabel: display.availabilityLabel,
  };
}
